import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import { useAudioRecording, TOO_SHORT_RECORDING, MAX_RECORDING_MS } from '@hook/iyagigo/useAudioTranscription';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import {
  useCheckWorksheetWorkbookUploadMutation,
  useWorksheetWorkbookUploadMutation,
  useWorksheetWorkbookTranscribeMutation,
} from '@hook/iyagigo/useApi';
import Gap from '@component/common/gap';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import TextBubble from '@design-system/text-bubble';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';
import { useAudioPlayer } from '@hook/common/useAudioPlayer';
import { addFurigana, createFuriganaMap, normalizeExplanation, replaceDashWithTilde } from '@util/string';
import {
  EApiLanguageCode,
  IConversationData,
  IConversationLine,
  ISourceConversationData,
} from '@datatype/iyagigo/api-type';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  workbookKey: string;
  languageCode: EApiLanguageCode;
  sourceConversation: ISourceConversationData;
  conversation: IConversationData;
  conversationAudioLinks: string[] | null;
  handleCompleteClick: () => void;
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 0 ${size.PAGE_PADDING};
`;
const ConversationItemsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_2};
  width: 100%;
  min-height: 100%;
  padding: ${size.MARGIN_2} ${size.MARGIN_1};
`;
const ControlContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex-shrink: 0;
  gap: ${size.MARGIN_1};
  width: 100%;
`;
const RecordingStatusContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  min-height: 50px;
`;
const WaveContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 24px;
`;
const WaveBar = styled.div<{ $height: number }>`
  width: 3px;
  height: ${({ $height }) => $height}px;
  border-radius: 2px;
  background-color: ${color.BROWN_1};
  transition: height 0.1s linear;
`;
const WAVE_BAR_WEIGHTS = [0.4, 0.7, 1, 0.7, 0.4];
const formatDuration = (durationMs: number) => {
  const seconds = Math.floor(durationMs / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
};

const MicrophoneContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  gap: ${size.MARGIN_1};
  position: relative;
  min-height: 60px;
  width: 100%;
`;
const MyRecordingCard = styled.div`
  position: absolute;
  z-index: 3;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
  padding: ${size.MARGIN_1};
  border: ${size.BORDER_WIDTH_1} solid ${color.BLUE_3};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.BLUE_5};
`;
const RecordingCardHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
`;
const NextControlContainer = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
`;
interface ILineControlProps {
  line?: IConversationLine;
  audioLink: string | null;
  isExpanded: boolean;
  isMyLine: boolean;
  handleExplanationClick: () => void;
}

const LineControlContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
`;

const ControlRow = styled.div<{ $isMyLine: boolean }>`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: ${(props) => (props.$isMyLine ? 'flex-end' : 'flex-start')};
  gap: ${size.MARGIN_1};
  margin: 5px 0 0 0;
`;

const ExplanationContainer = styled.div<{ $isMyLine: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  margin-top: 4px;
  padding: ${size.MARGIN_1};
  background-color: ${color.WHITE_1};
  border: ${size.BORDER_WIDTH_1} solid ${(props) => (props.$isMyLine ? color.BROWN_2 : color.GRAY_1)};
  border-radius: ${size.BORDER_RADIUS_1};
`;

const AudioContainer = styled.div`
  display: none;
`;

const LineControl: React.FC<ILineControlProps> = ({
  line,
  audioLink,
  isExpanded,
  isMyLine,
  handleExplanationClick,
}) => {
  const { isPlaying, audioRefs, handlePlayCompleted, handlePlayAndPauseClick } = useAudioPlayer({
    sources: audioLink ? [audioLink] : [],
  });

  return (
    <LineControlContainer>
      <ControlRow $isMyLine={isMyLine}>
        {audioLink && (
          <>
            <IconBlock
              icon={isPlaying ? 'volume-off' : 'volume-on'}
              design={{
                icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' },
                box: { event: { cursor: 'pointer' } },
              }}
              click={{ handler: handlePlayAndPauseClick }}
            />
            <AudioContainer>
              <audio
                ref={(element) => {
                  if (element) audioRefs.current[0] = element;
                }}
                src={audioLink}
                onEnded={handlePlayCompleted}
                controls={false}
              />
            </AudioContainer>
          </>
        )}
        {(line?.translation || line?.explanation) && (
          <IconBlock
            icon={isExpanded ? 'text-snippet-off' : 'text-snippet'}
            design={{
              icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' },
              box: { event: { cursor: 'pointer' } },
            }}
            click={{ handler: handleExplanationClick }}
          />
        )}
      </ControlRow>
      {isExpanded && (
        <ExplanationContainer $isMyLine={isMyLine}>
          {line?.translation && (
            <TextBlock
              text={normalizeExplanation(replaceDashWithTilde(line.translation), color.BLACK_2)}
              design={{
                text: { size: 'small', color: color.BLACK_2, bold: false },
                box: { alignX: 'left' },
              }}
            />
          )}
          {line?.explanation && (
            <>
              <Gap height={'5px'} />
              <TextBlock
                text={normalizeExplanation(replaceDashWithTilde(line.explanation), color.GRAY_1)}
                design={{
                  text: { size: 'small', color: color.GRAY_1, bold: false },
                  box: { alignX: 'left' },
                }}
              />
            </>
          )}
        </ExplanationContainer>
      )}
    </LineControlContainer>
  );
};

const SpeakingContent: React.FC<IProps> = ({
  workbookKey,
  languageCode,
  sourceConversation,
  conversation,
  conversationAudioLinks,
  handleCompleteClick,
}) => {
  const lines = useMemo(() => {
    const keyIndex = sourceConversation.keyLine?.index;
    if (keyIndex == null || !sourceConversation.line[keyIndex]) return [];
    return sourceConversation.line.slice(Math.max(0, keyIndex - 1), keyIndex + 1).map((line, index) => ({
      ...line,
      id: Math.max(0, keyIndex - 1) + index,
      sourceIndex: Math.max(0, keyIndex - 1) + index,
      isKeyLine: Math.max(0, keyIndex - 1) + index === keyIndex,
    }));
  }, [sourceConversation]);
  const [expandedLineId, setExpandedLineId] = useState<number | null>(null);
  const [complete, setComplete] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const activeAudioRef = useRef<HTMLAudioElement | null>(null);
  const recordingRequestRef = useRef(0);
  const recordingBusyRef = useRef(false);
  const [recordingState, setRecordingState] = useState<'idle' | 'processing'>('idle');

  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [recordingMessage, setRecordingMessage] = useState('');
  const [recordingCardOpen, setRecordingCardOpen] = useState(false);
  const { isPlaying, audioRefs, handlePlayCompleted, handlePlayAndPauseClick, handlePause } = useAudioPlayer({
    sources: recordingUrl ? [recordingUrl] : [],
  });
  const checkUploadMutation = useCheckWorksheetWorkbookUploadMutation(workbookKey);
  const uploadMutation = useWorksheetWorkbookUploadMutation(workbookKey);
  const transcribeMutation = useWorksheetWorkbookTranscribeMutation(workbookKey);
  const transcript = transcribeMutation.data?.data.text ?? '';
  const {
    microphoneState,
    durationMs,
    metering,
    isFinishingRecording,
    startRecording,
    finishRecording,
    cancelRecording,
    checkMicrophonePermission,
    openSystemSetting,
  } = useAudioRecording<string>({
    createUpload: async (format) => {
      const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
      const { data: target } = await uploadMutation.mutateAsync({ studentKey, format });
      return { ...target, context: studentKey };
    },
    checkUpload: async (studentKey) => {
      await checkUploadMutation.mutateAsync({ studentKey });
    },
  });
  const recording = microphoneState === 'recording';
  const transcribing = recordingState === 'processing';
  const recordingBusy = recordingState !== 'idle' || isFinishingRecording;

  useEffect(() => {
    checkMicrophonePermission();
    return () => {
      activeAudioRef.current?.pause();
      recordingRequestRef.current += 1;
      cancelRecording();
    };
  }, [cancelRecording, checkMicrophonePermission]);
  useEffect(() => {
    if (microphoneState === 'idle') return;
    if (microphoneState === 'denied' || microphoneState === 'blocked') {
      setRecordingMessage('마이크 권한을 허용한 뒤 다시 말해 주세요.');
    } else if (microphoneState === 'error') {
      setRecordingMessage('마이크 권한과 연결을 확인한 뒤 다시 시도해 주세요.');
    }
  }, [microphoneState]);
  useEffect(
    () => () => {
      if (recordingUrl?.startsWith('blob:')) URL.revokeObjectURL(recordingUrl);
    },
    [recordingUrl],
  );
  const myTurn = !complete && lines.some((line) => line.isKeyLine);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [complete]);

  const handleNext = () => {
    if (complete || recordingBusyRef.current || recording || recordingBusy) return;
    setRecordingUrl(null);
    transcribeMutation.reset();
    setRecordingMessage('');
    setComplete(true);
  };

  const handleRecord = async () => {
    if (recordingBusyRef.current || recordingBusy || !workbookKey) return;
    if (microphoneState === 'blocked') {
      openSystemSetting();
      return;
    }
    recordingBusyRef.current = true;
    const request = ++recordingRequestRef.current;
    try {
      if (recording) {
        setRecordingState('processing');
        const result = await finishRecording();
        if (request !== recordingRequestRef.current) return;
        if (result === TOO_SHORT_RECORDING) {
          setRecordingMessage('너무 짧아요. 다시 읽어 보세요.');
          return;
        }
        if (!result) throw new Error('recording upload failed');
        const response = await transcribeMutation.mutateAsync({ studentKey: result.context });
        if (request !== recordingRequestRef.current) return;
        if (!response.data.text?.trim()) throw new Error('empty transcription');
        setRecordingUrl(result.blob ? URL.createObjectURL(result.blob) : response.data.audioLink);
        setRecordingCardOpen(true);
        setRecordingMessage('');
      } else {
        activeAudioRef.current?.pause();
        setRecordingUrl(null);
        transcribeMutation.reset();
        setRecordingMessage('');
        await startRecording();
      }
    } catch (error) {
      if (request === recordingRequestRef.current) {
        const code = (error as { response?: { data?: { code?: string } } }).response?.data?.code;
        setRecordingMessage(
          code === 'CLIENT_0006'
            ? '목소리가 들리지 않았어요. 다시 말해 주세요.'
            : '말을 인식하지 못했어요. 다시 말해 주세요.',
        );
      }
    } finally {
      if (request === recordingRequestRef.current) {
        recordingBusyRef.current = false;
        setRecordingState('idle');
      }
    }
  };

  useEffect(() => {
    if (recording && durationMs >= MAX_RECORDING_MS && !recordingBusy) void handleRecord();
  }, [recording, durationMs, recordingBusy]);

  const guideText = transcribing
    ? '말을 듣고 있어요'
    : recording
      ? '말을 마치면 정지 버튼을 눌러 주세요'
      : recordingBusy
        ? '마이크를 준비하고 있어요'
        : recordingMessage ||
          (transcript ? '다시 말하려면 마이크를 눌러주세요' : '마이크를 누르고 오른쪽 대사를 읽어 보세요');

  const ConversationItems = lines.map((line) => {
    const speaker = sourceConversation.speaker.find((item) => item.id === line.speaker);
    const isMyLine = line.isKeyLine;
    const audioLink = conversationAudioLinks?.[line.sourceIndex];
    return (
      <TextBubble
        key={line.id}
        type='plain'
        text={
          languageCode === EApiLanguageCode.JAPANESE && line.pronunciation
            ? addFurigana(line.sentence, createFuriganaMap(line.sentence, line.pronunciation), 'start')
            : line.sentence
        }
        theme={isMyLine ? ETheme.BROWN : ETheme.GRAY}
        align={isMyLine ? 'right' : 'left'}
        speakerName={speaker?.name}
        profileEmoji={
          speaker?.gender === '여' || speaker?.gender === 'female'
            ? '👩🏻'
            : speaker?.gender === '남' || speaker?.gender === 'male'
              ? '🧑🏻'
              : '🙂'
        }
        lineMaxWidth='calc(100% - 50px)'
        control={
          <LineControl
            line={conversation.line[line.sourceIndex]}
            audioLink={audioLink ?? null}
            isExpanded={expandedLineId === line.id}
            isMyLine={isMyLine}
            handleExplanationClick={() => setExpandedLineId((value) => (value === line.id ? null : line.id))}
          />
        }
      />
    );
  });

  return (
    <Wrapper
      onPlayCapture={(event) => {
        const audio = event.target;
        if (!(audio instanceof HTMLAudioElement)) return;
        if (activeAudioRef.current && activeAudioRef.current !== audio) activeAudioRef.current.pause();
        activeAudioRef.current = audio;
      }}>
      <TextBlock
        text={conversation.situation}
        design={{ text: { size: 'small', color: color.GRAY_1 }, box: { alignX: 'left' } }}
      />
      <Gap height={size.MARGIN_1} />
      <ScrollContainer style={{ position: 'relative', backgroundColor: color.BROWN_5 }}>
        <Scroll direction='y' height='100%'>
          <ConversationItemsContainer>
            {ConversationItems}
            {complete && (
              <TextBlock
                text='대화 따라 읽기를 마쳤어요!'
                design={{ text: { size: 'large', color: color.BROWN_1, bold: true }, box: { alignX: 'center' } }}
              />
            )}
            <div ref={bottomRef} />
          </ConversationItemsContainer>
        </Scroll>
        {myTurn && recordingUrl && transcript && recordingCardOpen && (
          <MyRecordingCard>
            <RecordingCardHeader>
              <IconTextBlock
                icon='message-circle-text'
                text='이렇게 말했어요'
                design={{
                  text: { size: 'small', color: color.BLUE_2, bold: true },
                  icon: { size: 'tiny', color: color.BLUE_2, kind: 'outlined' },
                  box: { alignX: 'left', gap: '2px' },
                }}
              />
              <IconBlock
                icon='close'
                design={{
                  icon: { size: 'small', color: color.BLUE_2, kind: 'outlined' },
                  box: { event: { cursor: 'pointer' } },
                }}
                click={{
                  handler: () => {
                    handlePause();
                    setRecordingCardOpen(false);
                  },
                }}
              />
            </RecordingCardHeader>
            <TextBlock
              text={transcript}
              design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
            />
            <IconTextBlock
              icon={isPlaying ? 'volume-off' : 'volume-on'}
              text={isPlaying ? '듣기 멈추기' : '내 목소리 듣기'}
              click={{ handler: handlePlayAndPauseClick }}
              design={{
                text: { size: 'medium', color: color.WHITE_1 },
                icon: { size: 'small', color: color.WHITE_1, kind: 'outlined' },
                box: {
                  width: '100%',
                  padding: '5px 0',
                  backgroundColor: color.BLUE_2,
                  borderRadius: size.BORDER_RADIUS_1,
                  event: { cursor: 'pointer' },
                  gap: '5px',
                },
              }}
            />
          </MyRecordingCard>
        )}
      </ScrollContainer>
      {recordingUrl && (
        <AudioContainer>
          <audio
            key={recordingUrl}
            ref={(element) => {
              if (element) audioRefs.current[0] = element;
            }}
            src={recordingUrl}
            onEnded={handlePlayCompleted}
          />
        </AudioContainer>
      )}
      <Gap height={size.MARGIN_1} />
      <ControlContainer>
        {myTurn && (
          <RecordingStatusContainer>
            {recording ? (
              <>
                <WaveContainer>
                  {WAVE_BAR_WEIGHTS.map((weight, index) => {
                    const level = Number.isFinite(metering) ? Math.min(1, Math.max(0, (metering + 50) / 50)) : 0;
                    return <WaveBar key={index} $height={4 + 20 * level * weight} />;
                  })}
                </WaveContainer>
                <TextBlock
                  text={`${formatDuration(durationMs)} / ${formatDuration(MAX_RECORDING_MS)}`}
                  design={{ text: { size: 'small', color: color.BROWN_1, bold: true } }}
                />
              </>
            ) : (
              <TextBlock
                text={guideText}
                design={{
                  text: { size: 'medium', color: recordingMessage ? color.RED_2 : color.BROWN_1 },
                  box: {
                    alignX: 'center',
                    padding: `5px ${size.MARGIN_1}`,
                    borderWidth: size.BORDER_WIDTH_1,
                    borderColor: recordingMessage ? color.RED_2 : color.BROWN_1,
                    borderRadius: size.BORDER_RADIUS_2,
                  },
                }}
              />
            )}
          </RecordingStatusContainer>
        )}
        {myTurn && (
          <MicrophoneContainer>
            <IconBlock
              icon={recording ? 'stop' : 'mic'}
              design={{
                icon: { size: 'large', color: color.WHITE_1, kind: recording ? 'filled' : 'outlined' },
                box: {
                  width: '60px',
                  height: '60px',
                  alignX: 'center',
                  alignY: 'center',
                  borderRadius: '50%',
                  backgroundColor: recording ? color.RED_2 : color.BROWN_1,
                  opacity: recordingBusy ? 0.4 : 1,
                  event: { cursor: recordingBusy ? 'auto' : 'pointer' },
                },
              }}
              click={{
                handler: () => {
                  void handleRecord();
                },
              }}
            />
            <NextControlContainer>
              <TextButton
                text='끝내기'
                theme={ETheme.BROWN}
                kind='filled'
                size='medium'
                shape='square'
                width='80px'
                disabled={recording || recordingBusy}
                handleClick={handleCompleteClick}
              />
            </NextControlContainer>
          </MicrophoneContainer>
        )}
        {!myTurn && (
          <TextButton
            text={complete ? '오늘 학습 마치기' : '다음 대사'}
            theme={ETheme.BROWN}
            kind='filled'
            size='medium'
            shape='pill'
            width='100%'
            handleClick={complete ? handleCompleteClick : handleNext}
          />
        )}
      </ControlContainer>
    </Wrapper>
  );
};

export default SpeakingContent;
